import { useSelector } from "react-redux";

import {
  DeploymentPermission,
  LivestockSalePermission,
  SaleLotPermission,
  SaleyardPermission,
} from "constants/permissions";
import { SaleType, SaleyardSaleTypes } from "constants/sale";

import { hasPermission, someHasPermission } from "lib/permissions";

import {
  getActiveLivestockAgentDeployment,
  getActiveSaleyardAdminSaleyard,
  getCurrentSale,
  getIsConsigningAgentBySaleId,
  getIsCurrentSaleClearingSale,
  getIsLivestockAgent,
  getIsSaleyardAdmin,
  getSaleById,
  getSaleTypes,
  selectCurrentSaleyard,
  selectRoleCurrentDeployments,
  getSaleLots,
  getSideLoadedBusinessesCount,
} from "selectors";

/**
 * Tests whether the Model instance returned by the objectSelector contains the permission specified by the permissionRequired argument
 * @param {function(state: State): SelectedState} objectSelector
 * @param {String} permissionRequired
 * @template State, SelectedState
 * @returns {boolean}
 */
export function useHasPermission(objectSelector, permissionRequired) {
  return useSelector(state =>
    hasPermission(objectSelector(state), permissionRequired),
  );
}

/**
 * Tests whether any Model instance in the array returned by the objectSelector argument contains the permission specified by the permissionRequired argument
 * @param {function(state: State): SelectedState[]} objectSelector
 * @param {String} permissionRequired
 * @template State, SelectedState
 * @returns {boolean}
 */
export function useSomeHasPermission(objectSelector, permissionRequired) {
  return useSelector(state =>
    someHasPermission(objectSelector(state), permissionRequired),
  );
}

/**
 * Tests whether all Model instances in a lookup returned lookupSelector argument contains the permission specified by the permissionRequired argument
 * @param {function(state: State): SelectedState[]} lookupSelector
 * @param {String} permissionRequired
 * @param {function(obj): Boolean} filterFn
 * @template State, SelectedState
 * @returns {boolean}
 */
const defaultFilterFn = () => true;

export function useAllHasPermission(
  lookupSelector,
  permissionRequired,
  filterFn = defaultFilterFn,
) {
  return useSelector(state =>
    Object.values(lookupSelector(state))
      .filter(filterFn)
      .every(obj => hasPermission(obj, permissionRequired)),
  );
}

// Common usages

export function useHasDeploymentPermission(deploymentPermission) {
  return useHasPermission(
    getActiveLivestockAgentDeployment,
    deploymentPermission,
  );
}

export function useHasLivestockSalePermission(salePermission) {
  return useHasPermission(getCurrentSale, salePermission);
}

export function useHasSaleyardPermission(saleyardPermission) {
  return useHasPermission(selectCurrentSaleyard, saleyardPermission);
}

export function useHasSaleyardAdminSaleyardPermission(saleyardPermission) {
  return useHasPermission(getActiveSaleyardAdminSaleyard, saleyardPermission);
}

export function useHasSaleyardOrDeploymentPermission(
  deploymentPermission,
  saleyardPermission,
) {
  const isLivestockAgent = useSelector(getIsLivestockAgent);
  const selectedPermission = isLivestockAgent
    ? deploymentPermission
    : saleyardPermission;

  const parentObjectSelector = isLivestockAgent
    ? getActiveLivestockAgentDeployment
    : getActiveSaleyardAdminSaleyard;

  return useHasPermission(parentObjectSelector, selectedPermission);
}

export function useHasAddConsignmentPermission() {
  return useSelector(state =>
    someHasPermission(
      selectRoleCurrentDeployments(state),
      DeploymentPermission.canAddConsignment,
    ),
  );
}

export function useHasEmailListPermission() {
  return useHasSaleyardOrDeploymentPermission(
    DeploymentPermission.featureEmailList,
    SaleyardPermission.featureEmailList,
  );
}

export function useHasReceivalOrPenScanLotsPermission() {
  const hasReceivalLotPermission = useHasSaleyardPermission(
    SaleyardPermission.featureReceivalLots,
  );
  const hasPenScanPermission = useHasSaleyardPermission(
    SaleyardPermission.featurePenScanLots,
  );
  return hasReceivalLotPermission || hasPenScanPermission;
}

export function useHasMultiSaleReportPermission() {
  return useHasSaleyardOrDeploymentPermission(
    DeploymentPermission.featureMultiSaleReports,
    SaleyardPermission.featureMultiSaleReports,
  );
}

export function useCanCreateSellViaExAgentSaleFromSale(saleId) {
  const isLivestockAgent = useSelector(getIsLivestockAgent);
  const hasFeaturePermission = useHasDeploymentPermission(
    DeploymentPermission.featureCreateSellViaExAgentSaleFromSale,
  );
  const saleTypes = useSelector(getSaleTypes);
  const isSaleTypeEnabled = saleTypes.includes(
    SaleType.SELL_VIA_EXTERNAL_AGENT,
  );
  const isConsigningAgent = useSelector(getIsConsigningAgentBySaleId(saleId));
  const sourceSale = useSelector(getSaleById(saleId));

  if (!isLivestockAgent || !hasFeaturePermission || !isSaleTypeEnabled) {
    return false;
  }

  // Consigning agents can create from any sale type; copying one of your own
  // deployment's sales is restricted to saleyard sale types.
  return isConsigningAgent || SaleyardSaleTypes.includes(sourceSale?.sale_type);
}

export function useHasEditWeightOnEIDPermission() {
  return useHasSaleyardPermission(SaleyardPermission.featureEditWeightOnEid);
}

export const useHasAddAttachmentWithoutImagePermission = () => {
  const isSaleyardAdmin = useSelector(getIsSaleyardAdmin);
  const isLivestockAgent = useSelector(getIsLivestockAgent);

  let hasAddAttachmentWithoutImagePermission = false;

  const hasSaleyardAddAttachmentWithoutImagePermission =
    useHasSaleyardPermission(SaleyardPermission.canAddAttachmentWithoutImage);
  const hasDeploymentAddAttachmentWithoutImagePermission =
    useHasDeploymentPermission(
      DeploymentPermission.canAddAttachmentWithoutImage,
    );

  if (isSaleyardAdmin) {
    hasAddAttachmentWithoutImagePermission =
      hasSaleyardAddAttachmentWithoutImagePermission;
  }

  if (isLivestockAgent) {
    hasAddAttachmentWithoutImagePermission =
      hasDeploymentAddAttachmentWithoutImagePermission;
  }

  return hasAddAttachmentWithoutImagePermission;
};

export const useHasExportBusinessesPermission = () =>
  useHasSaleyardOrDeploymentPermission(
    DeploymentPermission.canExportBusinesses,
    SaleyardPermission.canExportBusinesses,
  );

export const useHasSaleLotPermission = () =>
  useSomeHasPermission(
    state => Object.values(getSaleLots(state)),
    SaleLotPermission.update,
  );

export const useHasMLAPermission = () =>
  useHasSaleyardPermission(SaleyardPermission.featureMLASupplementaryData);

export const useHasDataIndicatorsPermission = () =>
  useHasSaleyardPermission(SaleyardPermission.featureDataIndicators);

export const useHasMLAOrDataPermission = () => {
  const hasMLAPermission = useHasMLAPermission();
  const hasDataIndicatorsPermission = useHasDataIndicatorsPermission();
  return hasMLAPermission || hasDataIndicatorsPermission;
};

// Dentition is a deployment-only feature; there is no saleyard equivalent, so it
// stays switched off for saleyard admins.
export const useHasDentitionPermission = () => {
  const isLivestockAgent = useSelector(getIsLivestockAgent);
  const hasDeploymentDentitionPermission = useHasDeploymentPermission(
    DeploymentPermission.featureDentition,
  );

  return isLivestockAgent && hasDeploymentDentitionPermission;
};

export const useHasNASPermissions = () => {
  const dentitonPermission = useHasDentitionPermission();
  const weightRangePermission = useHasSaleyardOrDeploymentPermission(
    DeploymentPermission.featureWeightRange,
    SaleyardPermission.featureWeightRange,
  );

  return dentitonPermission && weightRangePermission;
};

export const useHasManualChargesPermission = () =>
  useHasSaleyardOrDeploymentPermission(
    DeploymentPermission.featureManualCharges,
    SaleyardPermission.featureManualCharges,
  );

export const useHasBillingPermission = () =>
  useHasSaleyardOrDeploymentPermission(
    DeploymentPermission.featureBilling,
    SaleyardPermission.featureBilling,
  );

export const useHasBillingRelatedPermissions = () => {
  const hasBillingPermission = useHasBillingPermission();
  const hasManualChargesPermission = useHasManualChargesPermission();
  return hasBillingPermission || hasManualChargesPermission;
};

export const useHasBillingRunPermission = () =>
  useHasSaleyardOrDeploymentPermission(
    DeploymentPermission.featureBillingRun,
    SaleyardPermission.featureBillingRun,
  );

export const useHasExternalIntegrationsPermission = () =>
  useHasSaleyardOrDeploymentPermission(
    DeploymentPermission.featureExternalIntegrations,
    SaleyardPermission.featureExternalIntegrations,
  );

export const useCanLinkExternalIntegrationPermission = () =>
  useHasSaleyardOrDeploymentPermission(
    DeploymentPermission.canLinkExternalIntegration,
    SaleyardPermission.canLinkExternalIntegration,
  );

export const useCanCreateLedgerAccountPermission = () =>
  useHasSaleyardOrDeploymentPermission(
    DeploymentPermission.canCreateLedgerAccount,
    SaleyardPermission.canCreateLedgerAccount,
  );

export const useCanAddBillingRunPermission = () =>
  useHasSaleyardOrDeploymentPermission(
    DeploymentPermission.canAddBillingRun,
    SaleyardPermission.canAddBillingRun,
  );

export const useCanAddSaleStatusPermission = () =>
  useHasSaleyardOrDeploymentPermission(
    DeploymentPermission.canAddSaleStatus,
    SaleyardPermission.canAddSaleStatus,
  );

export const useCanAddBusinessLabelPermission = () =>
  useHasSaleyardOrDeploymentPermission(
    DeploymentPermission.canAddBusinessLabel,
    SaleyardPermission.canAddBusinessLabel,
  );

export const useHasTradingTermsPermission = () =>
  useHasSaleyardOrDeploymentPermission(
    DeploymentPermission.featureTradingTerms,
    SaleyardPermission.featureTradingTerms,
  );

export const useHasSellViaExAgentRebatesPermission = () =>
  useHasSaleyardOrDeploymentPermission(
    DeploymentPermission.featureSellViaExAgentRebates,
    SaleyardPermission.featureSellViaExAgentRebates,
  );

export const useHasTrackingCategoriesPermission = () =>
  useHasSaleyardOrDeploymentPermission(
    DeploymentPermission.featureTrackingCategories,
    SaleyardPermission.featureTrackingCategories,
  );

export const useHasRetainedProceedsPermission = () =>
  useHasSaleyardOrDeploymentPermission(
    DeploymentPermission.featureRetainedProceeds,
    SaleyardPermission.featureRetainedProceeds,
  );

export const useHasXeroIntegrationPermission = () =>
  useHasSaleyardOrDeploymentPermission(
    DeploymentPermission.featureXeroIntegration,
    SaleyardPermission.featureXeroIntegration,
  );

export const useHasBuyerSplitPermission = () =>
  useHasSaleyardOrDeploymentPermission(
    DeploymentPermission.featureBuyerSplits,
    SaleyardPermission.featureBuyerSplits,
  );

export const useHasVendorSplitPermission = () =>
  useHasSaleyardOrDeploymentPermission(
    DeploymentPermission.featureVendorSplits,
    SaleyardPermission.featureVendorSplits,
  );

// SaleyardAdmins don't have access to VendorCommissions
// only check for DeploymentPermission here.
export const useHasVendorCommissionPermission = () =>
  useHasDeploymentPermission(DeploymentPermission.featureVendorCommissions);

export const useCanAddVendorCommissionPermission = () =>
  useHasLivestockSalePermission(LivestockSalePermission.canAddVendorCommission);

// SaleyardAdmins don't have access to DefaultVendorSplits
// only check for DeploymentPermission here.
export const useHasDefaultVendorSplitPermission = () =>
  useHasDeploymentPermission(DeploymentPermission.featureVendorSplits);

export const useHasDefaultBuyerSplitPermission = () =>
  useHasDeploymentPermission(DeploymentPermission.featureBuyerSplits);

export const useHasGSTOverridePermission = () =>
  useHasSaleyardOrDeploymentPermission(
    DeploymentPermission.featureGSTOverride,
    SaleyardPermission.featureGSTOverride,
  );

export const useHasFinancialSaleSubTypesPermission = () =>
  useHasSaleyardOrDeploymentPermission(
    DeploymentPermission.featureFinancialSaleSubTypes,
    SaleyardPermission.featureFinancialSaleSubTypes,
  );

export const useHasPaymentPreferenceForBusinessPermission = () =>
  useHasSaleyardOrDeploymentPermission(
    DeploymentPermission.canAddPaymentPreferenceForBusiness,
    SaleyardPermission.canAddPaymentPreferenceForBusiness,
  );

export const useHasSideLoadedBusinessPermission = (
  shortCircuitIfNoSideLoadedBusinesses = true,
) => {
  // Shortcircuit the permission check - if the user has no sideloaded businesses, don't bother checking the permission,
  // and hide the feature..
  // Allow overriding this behaviour by passing in false if this is used as a check for whether
  // to hit the api.
  const numberOfSideLoadedBusinesses = useSelector(
    getSideLoadedBusinessesCount,
  );
  const hasSideLoadedBusinessPermission = useHasSaleyardOrDeploymentPermission(
    DeploymentPermission.featureSideLoadedDataSets,
    SaleyardPermission.featureSideLoadedDataSets,
  );
  if (
    shortCircuitIfNoSideLoadedBusinesses &&
    numberOfSideLoadedBusinesses === 0
  ) {
    return false;
  }
  return hasSideLoadedBusinessPermission;
};

export const useHasAlternativeMobileWorkflowPermission = () =>
  useHasSaleyardOrDeploymentPermission(
    DeploymentPermission.featureAlternativeMobileWorkflow,
    SaleyardPermission.featureAlternativeMobileWorkflow,
  );

// Billing Document assignment applies only to Clearing Sales, and only on
// deployments with the feature enabled. The payments table column and the
// Edit Payment form's picker both call this hook. This keeps the two from
// drifting apart.
export const useShowBillingDocumentField = () => {
  const isClearingSale = useSelector(getIsCurrentSaleClearingSale);
  const hasBillingRunDocuments = useHasDeploymentPermission(
    DeploymentPermission.featureBillingRunDocuments,
  );
  return isClearingSale && hasBillingRunDocuments;
};
